import {
  fetchPolicyExtractedDataByRequestIdRequest,
  postPolicyDocumentExtractRequest,
  postPresignedUrlForClientByClientIdRequest,
  postPolicyImagePresignedUrlsForClientByClientIdRequest,
  resetPolicyExtractorState,
  selectClient,
  selectPolicyExtractor,
  setStatus,
  setUploadMode,
  setImageUploadComplete,
} from '@client/model';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { usePolling } from '@client/hooks/usePolling';
import { PdfFileUploader } from './PdfFileUploader';
import { ImageFileUploader } from './ImageFileUploader';
import { uploadFileToS3, uploadImagesToS3 } from '@client/util/uploadFileToBucket';
import { Button } from './ui/button';
import { PolicyAnalysisErrorAlert, PolicyAnalysisTable } from './PolicyExtractorResults';
import { PolicyExtractorProgressBar } from './PolicyExtractorProgressBar';
import { getCoupleLtcStartData } from '@shared/coupleLtcStartData';
import { ALLOWED_IMAGE_TYPES, MAX_IMAGES, MAX_IMAGE_SIZE_BYTES, newUuid } from '@shared';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import type { AppState } from '@client/model/store';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

const PDF_MIME = 'application/pdf';
const PDF_MAX_MB = 30;
const NEUTRAL_ACCEPT = 'application/pdf,.pdf,.jpg,.jpeg,.png';

type PolicyUploadProps = {
  resetState: () => void;
  policyFile: File | null;
  setPolicyFile: (file: File | null) => void;
};

function PolicyUpload({ resetState, policyFile, setPolicyFile }: PolicyUploadProps) {
  const [showRemoveFileWarning, setShowRemoveFileWarning] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const client = useSelector(selectClient);
  const { status, processedResponse, errors, requestId, uploadMode, imagePresignedUrls } =
    useSelector(selectPolicyExtractor);
  const maybeImageBasedPdfError = errors.length > 0 && errors[0] === 'Extracted text is too short';
  const isLoading =
    status === 'initiated' || status === 'polling' || status === 'fileUploadComplete';
  const hasFile = uploadMode === 'pdf' ? !!policyFile : imageFiles.length > 0;
  const showUploadButton = hasFile && !isLoading && !processedResponse;
  const showProgressBar = isLoading && !processedResponse;
  const dispatch = useDispatch();

  useDispatchPolicyExtractRunEffect();
  useUploadPdfEffect(policyFile);
  useUploadImagesEffect(imageFiles, imagePresignedUrls);

  usePolling({
    pollingFunction: () => {
      dispatch(fetchPolicyExtractedDataByRequestIdRequest({ requestId }));
    },
    shouldPoll: status === 'polling',
    interval: 5000,
    dependencies: [requestId],
  });

  function handleFileUpload() {
    if (uploadMode === 'pdf') {
      if (maybeImageBasedPdfError) {
        setPolicyFile(null);
        dispatch(resetPolicyExtractorState());
        return;
      }
      if (!policyFile) return;
      dispatch(
        postPresignedUrlForClientByClientIdRequest({
          clientId: client.clientId,
          originalFileName: policyFile.name,
        }),
      );
    } else {
      if (imageFiles.length === 0) return;
      dispatch(
        postPolicyImagePresignedUrlsForClientByClientIdRequest({
          clientId: client.clientId,
          requestId: newUuid(),
          imageCount: imageFiles.length,
          imageContentTypes: imageFiles.map(f => f.type),
        }),
      );
    }
  }

  const onPolicyFileChange = (file: File | null) => {
    if (file) {
      setPolicyFile(file);
      setShowRemoveFileWarning(false);
    } else {
      if (processedResponse) {
        setShowRemoveFileWarning(true);
      } else {
        resetState();
      }
    }
  };

  const onImageFilesChange = (files: File[]) => {
    setImageFiles(files);
    if (files.length === 0 && !processedResponse) {
      resetState();
    }
  };

  const handleNeutralFiles = (incoming: FileList | File[]) => {
    const list = Array.from(incoming);
    if (list.length === 0) return;
    const first = list[0]!;

    if (first.type === PDF_MIME) {
      if (first.size > PDF_MAX_MB * 1024 * 1024) {
        toast.error(`File size exceeds ${PDF_MAX_MB}MB. Please upload a smaller file.`);
        return;
      }
      dispatch(setUploadMode('pdf'));
      setPolicyFile(first);
      return;
    }

    const isImage = (t: string) => (ALLOWED_IMAGE_TYPES as readonly string[]).includes(t);
    if (isImage(first.type)) {
      const valid: File[] = [];
      const sizeErrors: string[] = [];
      for (const f of list) {
        if (!isImage(f.type)) continue;
        if (f.size > MAX_IMAGE_SIZE_BYTES) {
          sizeErrors.push(
            `${f.name}: exceeds ${(MAX_IMAGE_SIZE_BYTES / 1024 / 1024).toFixed(0)}MB limit.`,
          );
          continue;
        }
        valid.push(f);
      }
      if (sizeErrors.length > 0) {
        toast.error(sizeErrors.length === 1 ? sizeErrors[0]! : "Some images couldn't be added", {
          description: sizeErrors.length > 1 ? sizeErrors.join('\n') : undefined,
        });
      }
      const trimmed = valid.slice(0, MAX_IMAGES);
      if (trimmed.length < valid.length) {
        toast.warning(`Only the first ${MAX_IMAGES} images were added.`);
      }
      if (trimmed.length === 0) return;
      dispatch(setUploadMode('images'));
      setImageFiles(trimmed);
      return;
    }

    toast.error(
      `${first.type || 'Unknown file type'} is not supported. Please upload a PDF or image (JPEG/PNG).`,
    );
  };

  const getButtonLabel = () => {
    if (uploadMode === 'pdf' && maybeImageBasedPdfError) return 'Upload New PDF';
    if (status === 'error') return 'Try again';
    return 'Analyze Policy';
  };

  return (
    <div className="mb-12 flex flex-col gap-4">
      {!hasFile ? (
        <NeutralDropZone
          onFilesSelected={handleNeutralFiles}
          disabled={isLoading}
        />
      ) : (
        <div className="border-primary-200 flex flex-col gap-4 rounded-lg border p-5">
          {uploadMode === 'pdf' ? (
            <PdfFileUploader
              file={policyFile}
              setFile={onPolicyFileChange}
              maxFileSizeInMB={PDF_MAX_MB}
              hideRemove={isLoading || !!processedResponse}
            />
          ) : (
            <ImageFileUploader
              files={imageFiles}
              setFiles={onImageFilesChange}
              disabled={isLoading || !!processedResponse}
            />
          )}
        </div>
      )}
      {showProgressBar && <PolicyExtractorProgressBar />}
      <PolicyAnalysisErrorAlert />
      {errors.length === 0 && <PolicyAnalysisTable />}
      <UnsavedChangesDialog
        showWarning={showRemoveFileWarning}
        handleCancelClose={() => {
          setShowRemoveFileWarning(false);
        }}
        handleConfirmClose={() => {
          setShowRemoveFileWarning(false);
          resetState();
          setPolicyFile(null);
          setImageFiles([]);
        }}
        primaryButtonText={uploadMode === 'images' ? 'Remove upload' : 'Remove file'}
        secondaryButtonText="Keep upload"
        dialogTitle={uploadMode === 'images' ? 'Remove policy images' : 'Remove policy file'}
        dialogDescription="You will lose the current upload and any analysis results. Are you sure?"
      />
      {showUploadButton && (
        <Button
          className="w-full rounded-full font-medium"
          onClick={handleFileUpload}
        >
          {getButtonLabel()}
        </Button>
      )}
    </div>
  );
}

export default PolicyUpload;

function NeutralDropZone({
  onFilesSelected,
  disabled,
}: {
  onFilesSelected: (files: FileList | File[]) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(e.target.files);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFilesSelected(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <div className="col-span-full">
      <div
        className="flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <Upload
            aria-hidden="true"
            className="text-primary-500 mx-auto h-12 w-12"
          />
          <div className="mt-4 flex text-sm leading-6 text-gray-700">
            <label
              htmlFor="policy-upload"
              className="text-primary focus-within:ring-primary hover:text-primary-hover relative cursor-pointer rounded-md bg-white font-semibold focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2"
            >
              <span>Upload a file</span>
              <input
                ref={inputRef}
                onChange={handleChange}
                id="policy-upload"
                name="policy-upload"
                type="file"
                accept={NEUTRAL_ACCEPT}
                multiple
                disabled={disabled}
                className="sr-only"
              />
            </label>
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs leading-5 text-gray-700">
            PDF up to {PDF_MAX_MB}MB, or JPEG/PNG images (max {MAX_IMAGES})
          </p>
        </div>
      </div>
    </div>
  );
}

function useUploadPdfEffect(policyDocumentFile: File | null) {
  const dispatch = useDispatch();
  const { fileUrl, fileBucket, fileKey, uploadMode } = useSelector(selectPolicyExtractor);
  const isPresignedUrlReady = fileUrl && fileBucket && fileKey;

  return useEffect(() => {
    const uploadFile = async () => {
      if (uploadMode === 'pdf' && isPresignedUrlReady && policyDocumentFile) {
        await uploadFileToS3(fileUrl, policyDocumentFile, 'application/pdf');
        dispatch(setStatus('fileUploadComplete'));
      }
    };
    uploadFile();
  }, [isPresignedUrlReady, policyDocumentFile, uploadMode]);
}

function useUploadImagesEffect(
  imageFiles: File[],
  imagePresignedUrls: PolicyImagePresignedUrlItem[],
) {
  const dispatch = useDispatch();
  const { uploadMode } = useSelector(selectPolicyExtractor);

  return useEffect(() => {
    const upload = async () => {
      if (
        uploadMode === 'images' &&
        imagePresignedUrls.length > 0 &&
        imageFiles.length > 0 &&
        imagePresignedUrls.length === imageFiles.length
      ) {
        await uploadImagesToS3(imagePresignedUrls, imageFiles);
        dispatch(setImageUploadComplete());
      }
    };
    upload();
  }, [imagePresignedUrls, imageFiles, uploadMode]);
}

function useDispatchPolicyExtractRunEffect() {
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const status = useSelector(selectPolicyExtractor).status;

  useEffect(() => {
    if (status !== 'fileUploadComplete') return;

    const policy = selectPolicyExtractor(store.getState());
    const client = selectClient(store.getState());
    const coupleLtcStartData = getCoupleLtcStartData(client, client.mutableClientPartner);

    const requestId =
      policy.uploadMode === 'images' && policy.requestId ? policy.requestId : newUuid();

    const requestBody: PolicyUploadTextAnalysisRequest = {
      fileBucket: policy.uploadMode === 'pdf' ? policy.fileBucket : undefined,
      fileKey: policy.uploadMode === 'pdf' ? policy.fileKey : undefined,
      imageKeys: policy.uploadMode === 'images' ? policy.imageKeys : undefined,
      imageBucket: policy.uploadMode === 'images' ? policy.imageBucket : undefined,
      clientId: client.clientId,
      requestId,
      ltcAtAge: client.appliedInferenceSet.ltcAtAge,
      clientBirthYear: client.intakeSurvey.clientBirthYear,
      clientFullName: client.clientFullName,
      clientGenderAtBirth: client.intakeSurvey.clientGenderAtBirth,
      policyActivationAgeClientIsPolicyHolder: coupleLtcStartData
        ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(true)
        : undefined,
      policyActivationAgePartnerIsPolicyHolder: coupleLtcStartData
        ? coupleLtcStartData.getPolicyHolderAgeAtPolicyActivation(false)
        : undefined,
    };

    dispatch(postPolicyDocumentExtractRequest(requestBody));
  }, [dispatch, status, store]);
}
